import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { format, parseISO } from 'date-fns';
import CheckSVG from '@birdi/icons/check.svg';
import TargetSVG from '@birdi/icons/target.svg';
import { BirdiTheme, breakpoints, buttonBlue } from '@birdi/theme';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type {
  ToleranceAxis,
  ToleranceLevel,
  ToleranceLevelInput,
  ToleranceVersion,
} from '@birdi/js-sdk/src/public/map-sites';
import { useMetaSelector } from '../meta/reducer';
import {
  invalidateTolerances,
  useToleranceHistory,
  useTolerances,
} from './queries';
import { ModalShell } from './site-modals';
import { infoBannerCSS } from './styles';
import { readAPIErrorMessage } from './utils';

/**
 * The two levels the UI writes. The data model allows any number of levels per
 * axis (they are rows, not columns), but the design shows exactly these two and
 * there is no add/rename control yet — so the form is fixed while the API stays
 * general.
 */
const LEVEL_NAMES = ['Warning', 'Action'] as const;
type LevelName = (typeof LEVEL_NAMES)[number];

const AXES: { axis: ToleranceAxis; label: string }[] = [
  { axis: 'horizontal', label: 'Horizontal (XY)' },
  { axis: 'vertical', label: 'Vertical (Z)' },
];

/**
 * Band colours for the "how these read" key. Their own function rather than the
 * map's status palette: a movement band and a mark's status are different
 * things that happen to share a traffic light. Amber is a literal because the
 * theme has no warning token.
 */
const bandColours = (theme: BirdiTheme) => ({
  within: theme.flora,
  warning: '#F5C451',
  action: theme.crimsonSunbird,
});

/** Form state: one string per axis+level, because a partly-typed number is text. */
type Draft = Record<string, string>;

const key = (axis: ToleranceAxis, name: LevelName) => `${axis}:${name}`;

const findLevel = (
  levels: (ToleranceLevel | ToleranceLevelInput)[] | undefined,
  axis: ToleranceAxis,
  name: LevelName,
) =>
  levels?.find(
    (l) => l.axis === axis && l.name.toLowerCase() === name.toLowerCase(),
  );

/**
 * Seed the inputs from the saved version, falling back to the API's defaults.
 *
 * Deliberately NOT zero, which is what the Figma shows: an empty state of 0
 * reads as "no movement is tolerated", and a user who saved it unchanged would
 * put the whole site into Action. The defaults are the values already in force
 * for an unconfigured site, so showing them is the honest empty state
 * (Tom, 9 Sep 2026).
 */
const draftFrom = (
  current: ToleranceVersion | null | undefined,
  defaults: ToleranceLevelInput[] | undefined,
): Draft => {
  const draft: Draft = {};
  for (const { axis } of AXES) {
    for (const name of LEVEL_NAMES) {
      const level =
        findLevel(current?.levels, axis, name) ??
        findLevel(defaults, axis, name);
      draft[key(axis, name)] = level ? String(level.threshold) : '';
    }
  }
  return draft;
};

/**
 * Dimmed and inert without `prism-processing` — the same treatment the prism
 * reference set panel gets. Inert rather than merely faded: a dimmed but
 * clickable Save would write a tolerance for a prism network the org can't
 * upload. The info banner above stays at full strength, since it explains the
 * feature rather than offering it.
 */
const lockedCSS = css`
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
`;

const cardCSS = (theme: BirdiTheme) => css`
  background: ${theme.mono0};
  border: 1px solid ${theme.mono20};
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(9, 30, 66, 0.06);
  padding: 1.25rem 1.5rem 1.5rem;
`;

// Title + subtitle on the left, the "last changed" pill on the right. Wraps to
// its own line when the card is too narrow to hold both.
const cardHeaderCSS = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const headerTitleCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  h3 {
    margin: 0;
    color: ${theme.midnight};
    font-size: 1rem;
  }
  p {
    margin: 0.15rem 0 0;
    font-size: 0.85rem;
    color: ${theme.mono50};
  }
`;

// Sized to stand as tall as the title and subtitle it sits beside, rather than
// floating against the title alone.
const iconBoxCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  background: ${theme.mono10};
  color: ${theme.midnight};
  flex-shrink: 0;
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

// "Last changed by …" — flora on lightFlora with a tick, per the design.
const lastChangedCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  background: ${theme.lightFlora};
  color: ${theme.flora};
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  svg {
    width: 1.1em;
    height: 1.1em;
    flex-shrink: 0;
    /* check.svg ships at stroke-width 2, which reads thin at this size. */
    stroke-width: 3;
  }
`;

// A rule between the header and the fields — not a box around them.
const separatorCSS = (theme: BirdiTheme) => css`
  border: none;
  border-top: 1px solid ${theme.mono20};
  margin: 1rem -1.5rem 1.25rem;
`;

// Wraps rather than compressing: below about 46rem of card the key drops onto
// its own line instead of squeezing the fields (or overlapping them).
const bodyCSS = css`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1.5rem 2rem;
  @media (max-width: ${breakpoints.s}) {
    flex-direction: column;
    gap: 1.25rem;
  }
`;

/**
 * One row per axis, Warning and Action as columns — the axis is the thing being
 * described, the levels are its two values.
 */
const fieldGridCSS = css`
  /* Capped rather than 1fr: stretched to the full card the fields dwarfed the
     three-digit values they hold. The basis is what makes the key wrap. */
  flex: 1 1 34rem;
  display: grid;
  grid-template-columns: auto minmax(0, 16.5rem) minmax(0, 16.5rem);
  justify-content: start;
  align-items: center;
  gap: 0.75rem 1.5rem;
  @media (max-width: ${breakpoints.xs}) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const axisLabelCSS = (theme: BirdiTheme) => css`
  font-weight: 700;
  color: ${theme.midnight};
  white-space: nowrap;
  @media (max-width: ${breakpoints.xs}) {
    grid-column: 1 / -1;
  }
`;

const levelLabelCSS = (theme: BirdiTheme) => css`
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.85rem;
  color: ${theme.mono50};
`;

/**
 * Bordered box holding a borderless number input and the unit, so "0.075" and
 * "m" read as one control rather than a field with a label stuck beside it.
 */
const inputBoxCSS = (theme: BirdiTheme, disabled: boolean) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.mono40};
  border-radius: 6px;
  background: ${disabled ? theme.mono10 : theme.mono0};
  &:focus-within {
    border-color: ${theme.deepOcean};
    outline: 1px solid ${theme.deepOcean};
  }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.95rem;
    color: ${theme.mono70};
    /* Spinners would crowd the unit sitting in the same box. */
    appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }
  }
  span {
    color: ${theme.mono50};
    font-size: 0.85rem;
  }
`;

// Reads back the bands the four numbers actually produce, so the meaning of an
// edit is visible before it is saved.
const keyPanelCSS = (theme: BirdiTheme) => css`
  flex: 0 0 17rem;
  box-sizing: border-box;
  background: ${theme.mono10};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  h4 {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    color: ${theme.midnight};
  }
  @media (max-width: ${breakpoints.s}) {
    flex: 1 1 auto;
    width: 100%;
  }
`;

const keyRowCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: ${theme.mono70};
  & + & {
    margin-top: 0.5rem;
  }
`;

const dotCSS = (colour: string) => css`
  flex: 0 0 auto;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: ${colour};
`;

// Below the card: the history control (or the defaults note) on the left, Save
// on the right.
const cardFooterCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: ${theme.mono50};
`;

const linkButtonCSS = (theme: BirdiTheme) => css`
  background: none;
  border: none;
  padding: 0;
  color: ${theme.deepOcean};
  font-size: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

const saveButtonCSS = (theme: BirdiTheme) => css`
  ${buttonBlue(theme, { baseSize: 'sm' })};
  padding: 0 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
`;

const errorCSS = (theme: BirdiTheme) => css`
  margin: 1rem 0 0;
  font-size: 0.85rem;
  color: ${theme.crimsonSunbird};
`;

const historyRowCSS = (theme: BirdiTheme) => css`
  padding: 1rem 0;
  /* The modal already sets the title off with a rule, so the first entry does
     not need a second gap above it. */
  &:first-of-type {
    padding-top: 0.25rem;
  }
  & + & {
    border-top: 1px solid ${theme.mono20};
  }
`;

const historyHeadCSS = (theme: BirdiTheme) => css`
  margin-bottom: 0.65rem;
  /* Date (with its badge) on one line, author beneath — the two read as a
     heading and its byline rather than one long run of text. */
  .when {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  h5 {
    margin: 0;
    font-size: 0.95rem;
    color: ${theme.midnight};
  }
  .author {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.85rem;
    color: ${theme.mono50};
  }
`;

const currentPillCSS = (theme: BirdiTheme) => css`
  padding: 0.1rem 0.5rem;
  border-radius: 1rem;
  background: ${theme.lightFlora};
  color: ${theme.flora};
  font-size: 0.75rem;
  font-weight: 700;
`;

/**
 * Axes down, Warning/Action across — the same arrangement as the editor on the
 * tab, so a past version is read exactly where the live one is.
 */
const historyGridCSS = (theme: BirdiTheme) => css`
  display: grid;
  grid-template-columns: auto auto auto;
  gap: 0.5rem 2.5rem;
  justify-content: start;
  font-size: 0.85rem;
  border: 1px solid ${theme.mono30};
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  .head {
    font-weight: 700;
    color: ${theme.midnight};
  }
  .label {
    font-weight: 700;
    color: ${theme.midnight};
    /* Keeps the axis names clear of the numbers beside them. */
    padding-right: 1rem;
  }
  .value {
    color: ${theme.mono70};
    font-variant-numeric: tabular-nums;
  }
`;

const formatWhen = (iso: string) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return iso;
  }
};

const levelValue = (
  version: ToleranceVersion,
  axis: ToleranceAxis,
  name: LevelName,
) => {
  const level = findLevel(version.levels, axis, name);
  return level ? `${level.threshold} m` : '—';
};

const HistoryModal = ({
  siteId,
  open,
  setOpen,
}: {
  siteId: string;
  open: boolean;
  setOpen: (value: boolean) => void;
}) => {
  const { data: versions, isLoading } = useToleranceHistory(siteId, open);

  return (
    <ModalShell title="Tolerance history" open={open} setOpen={setOpen}>
      {isLoading && <p>Loading ...</p>}
      {!isLoading && !versions?.length && <p>No changes recorded yet.</p>}
      {versions?.map((version, index) => (
        <div css={historyRowCSS} key={version.id}>
          <div css={historyHeadCSS}>
            <div className="when">
              <h5>{formatWhen(version.createdAt)}</h5>
              {index === 0 && <span css={currentPillCSS}>Current</span>}
            </div>
            <span className="author">
              {version.createdByName || 'Unknown user'}
            </span>
          </div>
          <div css={historyGridCSS}>
            <span />
            {LEVEL_NAMES.map((name) => (
              <span className="head" key={name}>
                {name}
              </span>
            ))}
            {AXES.map(({ axis, label }) => (
              <React.Fragment key={axis}>
                <span className="label">{label}</span>
                {LEVEL_NAMES.map((name) => (
                  <span className="value" key={name}>
                    {levelValue(version, axis, name)}
                  </span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </ModalShell>
  );
};

export const TolerancesTab = ({
  siteId,
  locked = false,
}: {
  siteId: string;
  /**
   * No `prism-processing` on the plan. The tab still renders — the customer
   * should see what the plan would give them — but nothing in it can be used.
   */
  locked?: boolean;
}) => {
  const meta = useMetaSelector();
  const { data, isLoading } = useTolerances(siteId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isOps = meta.opsActions.includes('access_ops');
  // Same gate as editing the site itself and setting a mark's status — NOT an
  // org-admin permission (Tom, 9 Sep 2026). An unentitled plan can't edit at
  // all, whatever the permission says.
  const canEdit =
    !locked && (meta.permissions.includes('workspace:map:create') || isOps);

  const serverDraft = useMemo(
    () => draftFrom(data?.current, data?.defaults),
    [data],
  );
  // `draft` is null until the user types, so a background refetch doesn't clash
  // with what they're editing.
  const values = draft ?? serverDraft;
  const dirty = LEVEL_NAMES.some((name) =>
    AXES.some(
      ({ axis }) => values[key(axis, name)] !== serverDraft[key(axis, name)],
    ),
  );

  if (isLoading) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
        `}
      >
        Loading ...
      </div>
    );
  }

  const parsed = AXES.flatMap(({ axis }) =>
    LEVEL_NAMES.map((name) => ({
      axis,
      name,
      threshold: Number(values[key(axis, name)]),
      raw: values[key(axis, name)],
    })),
  );
  const allPositive = parsed.every(
    (l) =>
      l.raw.trim() !== '' && Number.isFinite(l.threshold) && l.threshold > 0,
  );
  // Mirrors the UNIQUE (version_id, axis, threshold) constraint: equal
  // thresholds on one axis leave the band order undefined.
  const axisCollision = AXES.some(({ axis }) => {
    const forAxis = parsed.filter((l) => l.axis === axis);
    return new Set(forAxis.map((l) => l.threshold)).size !== forAxis.length;
  });
  // Not a database rule, but a Warning that fires after Action is a mistake
  // every time — the bands are read in ascending order.
  const outOfOrder = AXES.some(({ axis }) => {
    const warning = parsed.find((l) => l.axis === axis && l.name === 'Warning');
    const action = parsed.find((l) => l.axis === axis && l.name === 'Action');
    return !!warning && !!action && warning.threshold > action.threshold;
  });

  const validationMessage = (() => {
    if (!allPositive) return 'Every tolerance must be a number greater than 0.';
    if (axisCollision) return 'Warning and Action must differ on each axis.';
    if (outOfOrder) return 'Warning must be lower than Action on each axis.';
    return null;
  })();

  const onSave = async () => {
    if (validationMessage) return;
    setSaving(true);
    setError(null);
    try {
      await mapSitesAPI.saveTolerances(
        stroot('tol3sv'),
        siteId,
        parsed.map(({ axis, name, threshold }) => ({ axis, name, threshold })),
      );
      // The save appended a version, so the current value AND the history (and
      // with it the tab's count) have both changed.
      invalidateTolerances(siteId);
      setDraft(null);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const current = data?.current ?? null;

  // The key reads back the bands the CURRENT inputs produce, so it follows an
  // edit rather than describing the saved version. Horizontal is the one shown:
  // the two axes usually match, and a key per axis would be four more rows for
  // a rule that is the same shape either way.
  const keyWarning = parsed.find(
    (l) => l.axis === 'horizontal' && l.name === 'Warning',
  );
  const keyAction = parsed.find(
    (l) => l.axis === 'horizontal' && l.name === 'Action',
  );
  const fmtBand = (l?: { threshold: number; raw: string }) =>
    l && l.raw.trim() !== '' && Number.isFinite(l.threshold)
      ? `${l.threshold} m`
      : '—';

  return (
    <div>
      <div css={infoBannerCSS}>
        <span>
          {/* The design said "organisation admins", but the gate is
              workspace:map:create — the same permission as editing the site —
              so that wording would tell a workspace user who CAN edit that they
              can't (Saghar, 10 Sep 2026). */}
          <strong>
            Only users who can edit this site can change trigger levels.
          </strong>{' '}
          Applied values are recorded on each report at generation. Editing here
          does not re-colour reports already generated.
        </span>
      </div>

      <div
        css={locked ? lockedCSS : undefined}
        data-locked={locked || undefined}
      >
        <section css={cardCSS} data-testid="site-tolerances-card">
          <header css={cardHeaderCSS}>
            <div css={headerTitleCSS}>
              <span css={iconBoxCSS}>
                <TargetSVG />
              </span>
              <div>
                <h3>Prism tolerances</h3>
                <p>
                  Set Trigger levels applied to prism movement in this
                  site&apos;s reports.
                </p>
              </div>
            </div>
            {current && (
              <span
                css={lastChangedCSS}
                data-testid="site-tolerances-last-changed"
              >
                <CheckSVG />
                Last changed by {current.createdByName || 'Unknown user'} ·{' '}
                {formatWhen(current.createdAt)}
              </span>
            )}
          </header>

          <hr css={separatorCSS} />

          <div css={bodyCSS}>
            <div css={fieldGridCSS}>
              {AXES.map(({ axis, label }) => (
                <React.Fragment key={axis}>
                  <span css={axisLabelCSS}>{label}</span>
                  {LEVEL_NAMES.map((name) => (
                    <div key={name}>
                      <label
                        css={levelLabelCSS}
                        htmlFor={`tolerance-${axis}-${name}`}
                      >
                        {name}
                      </label>
                      <div
                        css={(theme) => inputBoxCSS(theme, !canEdit || saving)}
                      >
                        <input
                          id={`tolerance-${axis}-${name}`}
                          data-testid={`tolerance-input-${axis}-${name.toLowerCase()}`}
                          type="number"
                          min="0"
                          step="0.001"
                          inputMode="decimal"
                          disabled={!canEdit || saving}
                          value={values[key(axis, name)]}
                          onChange={(event) =>
                            setDraft({
                              ...values,
                              [key(axis, name)]: event.target.value,
                            })
                          }
                        />
                        <span>m</span>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>

            <aside css={keyPanelCSS}>
              <h4>How these read on the report</h4>
              <div css={keyRowCSS}>
                <span css={(theme) => dotCSS(bandColours(theme).within)} />
                Within · below {fmtBand(keyWarning)}
              </div>
              <div css={keyRowCSS}>
                <span css={(theme) => dotCSS(bandColours(theme).warning)} />
                Warning · {fmtBand(keyWarning)} – {fmtBand(keyAction)}
              </div>
              <div css={keyRowCSS}>
                <span css={(theme) => dotCSS(bandColours(theme).action)} />
                Action · {fmtBand(keyAction)} and above
              </div>
            </aside>
          </div>

          {canEdit && dirty && validationMessage && (
            <p css={errorCSS}>{validationMessage}</p>
          )}
          {error && <p css={errorCSS}>{error}</p>}
        </section>

        <div css={cardFooterCSS}>
          {current ? (
            <button
              type="button"
              css={linkButtonCSS}
              data-testid="site-tolerances-history"
              onClick={() => setHistoryOpen(true)}
            >
              View history
            </button>
          ) : (
            <span>Not set for this site — showing the Birdi defaults.</span>
          )}
          {canEdit && (
            <button
              type="button"
              css={saveButtonCSS}
              data-testid="site-tolerances-save"
              disabled={saving || !dirty || !!validationMessage}
              onClick={onSave}
            >
              {saving ? 'Saving ...' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      <HistoryModal
        siteId={siteId}
        open={historyOpen}
        setOpen={setHistoryOpen}
      />
    </div>
  );
};
